/**
 * Shared utilities for resolving section instances from state.
 * Used by image-editor.js and property-panel.js to look up sections
 * by instanceId across both page sections and shared sections.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import {
  ENTRANCE_ELEMENT_FIELD,
  ENTRANCE_ELEMENT_DELAY_FIELD
} from '../shared/entrance-presets.js';

/**
 * EA-8 (contracts §1, element-path D9): an element ENTRANCE commit must
 * RERENDER, not just restyle — the stamp lives in markup, and the shell
 * deliberately treats override changes as CSS-only. One request per WRITE
 * (both writers below are single-store.update bulk paths), and only when an
 * entrance field is among the changes; a colour scrub stays render-free.
 */
function requestElementEntranceRerender(changeSets) {
  const touches = changeSets.some(
    (changes) =>
      changes && (ENTRANCE_ELEMENT_FIELD in changes || ENTRANCE_ELEMENT_DELAY_FIELD in changes)
  );
  if (touches) eventBus.emit('preview:render-request', { preserveScroll: true });
}

export const RESPONSIVE_VIEWPORTS = Object.freeze(['desktop', 'tablet', 'mobile']);

function hasValue(value) {
  return value !== undefined && value !== null && value !== '';
}

function cleanResponsiveBuckets(responsive) {
  const next = {};
  for (const viewport of RESPONSIVE_VIEWPORTS) {
    const bucket = responsive?.[viewport];
    if (!bucket || typeof bucket !== 'object') continue;
    const cleanBucket = { ...bucket };
    if (cleanBucket.elements && Object.keys(cleanBucket.elements).length === 0) {
      delete cleanBucket.elements;
    }
    if (Object.keys(cleanBucket).length > 0) next[viewport] = cleanBucket;
  }
  return next;
}

/** Return the effective section-level overrides at a named preview viewport. */
export function getSectionOverridesForViewport(section, viewport = null) {
  const base = section?.overrides || {};
  if (!viewport || !RESPONSIVE_VIEWPORTS.includes(viewport)) return base;
  return { ...base, ...(base.responsive?.[viewport] || {}) };
}

/** Return the effective element overrides at a named preview viewport. */
export function getElementOverridesForViewport(section, eid, viewport = null) {
  const base = section?.overrides || {};
  const baseProps = base.elements?.[eid] || {};
  if (!viewport || !RESPONSIVE_VIEWPORTS.includes(viewport)) return baseProps;
  return {
    ...baseProps,
    ...(base.responsive?.[viewport]?.elements?.[eid] || {})
  };
}

/** Return only the overrides explicitly stored in one responsive bucket. */
export function getExplicitElementOverrides(section, eid, viewport = null) {
  if (!viewport || !RESPONSIVE_VIEWPORTS.includes(viewport)) {
    return section?.overrides?.elements?.[eid] || {};
  }
  return section?.overrides?.responsive?.[viewport]?.elements?.[eid] || {};
}

/**
 * Fan-out resolver for descendant restyling (Polish 16C2), injected rather than
 * imported: the resolver needs `element-selection.js`, which reaches
 * `element-actions.js`, which imports THIS module — so a direct import would be
 * a cycle. Same shape as the injectable collab guard.
 *
 * `(instanceId, eid) => string[] | null` — the eids one write should cover.
 * Null (and the un-installed default) means "just this one", so solo behaviour
 * is byte-identical to before 16C2.
 * @type {null | ((instanceId: string, eid: string) => string[]|null)}
 */
let _restyleResolver = null;

/** Install the 16C2 fan-out resolver. Pass null to uninstall. */
export function setElementRestyleResolver(fn) {
  _restyleResolver = typeof fn === 'function' ? fn : null;
}

/**
 * EA-9: the READ-side twin of the writer's fan-out — the exact eids one
 * `applyElementOverrides(instanceId, eid, …)` call would touch right now.
 * A control that fans its writes must read the same set, or a mixed state
 * across sibling units renders as a confident single value.
 */
export function expandRestyleTargets(instanceId, eid) {
  if (!eid) return [];
  const fanned = _restyleResolver ? _restyleResolver(instanceId, eid) : null;
  return Array.isArray(fanned) && fanned.length ? fanned : [eid];
}

/**
 * PURE: fold one element's changes into an overrides object and report whether
 * anything moved. The single and the bulk writers both go through here, so the
 * shape rules — an all-size write clearing the same fields from named buckets,
 * empty objects collapsing away — cannot drift between them.
 *
 * @returns {{overrides: object, changed: boolean}} a NEW overrides object
 */
export function computeElementOverrides(baseOverrides, eid, changes, viewport = null) {
  const overrides = { ...(baseOverrides || {}) };
  const target = viewport ? { ...(overrides.responsive?.[viewport] || {}) } : overrides;
  const elements = { ...(target.elements || {}) };
  const element = { ...(elements[eid] || {}) };
  let changed = false;

  for (const [field, value] of Object.entries(changes)) {
    const previous = element[field];
    if (hasValue(value)) {
      if (!Object.is(previous, value)) {
        element[field] = value;
        changed = true;
      }
    } else if (Object.prototype.hasOwnProperty.call(element, field)) {
      delete element[field];
      if (element.__important && typeof element.__important === 'object') {
        const flags = { ...element.__important };
        delete flags[field];
        if (Object.keys(flags).length) element.__important = flags;
        else delete element.__important;
      }
      changed = true;
    }
  }

  if (Object.keys(element).length) elements[eid] = element;
  else delete elements[eid];
  if (Object.keys(elements).length) target.elements = elements;
  else delete target.elements;

  if (viewport) {
    const responsive = { ...(overrides.responsive || {}) };
    if (Object.keys(target).length) responsive[viewport] = target;
    else delete responsive[viewport];
    const cleaned = cleanResponsiveBuckets(responsive);
    if (Object.keys(cleaned).length) overrides.responsive = cleaned;
    else delete overrides.responsive;
  } else {
    const responsive = structuredClone(overrides.responsive || {});
    for (const bucketName of RESPONSIVE_VIEWPORTS) {
      const bucketElement = responsive[bucketName]?.elements?.[eid];
      if (!bucketElement) continue;
      for (const field of Object.keys(changes)) delete bucketElement[field];
      if (bucketElement.__important && typeof bucketElement.__important === 'object') {
        for (const field of Object.keys(changes)) delete bucketElement.__important[field];
        if (!Object.keys(bucketElement.__important).length) delete bucketElement.__important;
      }
      if (!Object.keys(bucketElement).length) delete responsive[bucketName].elements[eid];
    }
    const cleaned = cleanResponsiveBuckets(responsive);
    if (Object.keys(cleaned).length) overrides.responsive = cleaned;
    else delete overrides.responsive;
  }

  return { overrides, changed };
}

/**
 * Write the same change to N elements of ONE section as a SINGLE
 * `store.update` — one history entry, one notify, one collab op (Polish 16C2).
 *
 * All targets live in the same section's overrides object, so the fold is pure
 * and the write is one patch; there is no DOMParse here at all, which is what
 * keeps a 60 Hz scrub across a row of icons as cheap as a single-element one.
 */
export function applyElementOverridesAcross(instanceId, eids, changes, opts = {}) {
  const resolved = resolveSectionByInstance(instanceId);
  const list = (Array.isArray(eids) ? eids : [eids]).filter(Boolean);
  if (!resolved || list.length === 0 || !changes || typeof changes !== 'object') return false;

  const viewport = RESPONSIVE_VIEWPORTS.includes(opts.viewport) ? opts.viewport : null;
  let overrides = resolved.section.overrides || {};
  let changed = false;
  for (const eid of list) {
    const result = computeElementOverrides(overrides, eid, changes, viewport);
    overrides = result.overrides;
    if (result.changed) changed = true;
  }

  // Parity with the pre-16C2 single path: a base-size write commits even when
  // no value moved, because it is also what clears stale named-viewport values.
  if (!changed && viewport) return false;
  const nextOverrides = Object.keys(overrides).length ? overrides : null;
  store.update(
    resolved.overridesPath || `${resolved.sectionPath}.overrides`,
    nextOverrides,
    opts.label || 'Change element style'
  );
  for (const eid of list) {
    for (const field of Object.keys(changes)) {
      eventBus.emit('theme:override-changed', {
        scope: 'element',
        instanceId,
        eid,
        field,
        viewport,
        source: opts.source || 'element-controls'
      });
    }
  }
  requestElementEntranceRerender([changes]);
  return true;
}

/**
 * Write PER-EID-DIFFERING changes to N elements of ONE section as a SINGLE
 * `store.update` (UX Pack F4b — the distribute shape). Same fold discipline
 * as applyElementOverridesAcross, but each eid carries its own change set:
 * `changesByEid` is `{ [eid]: {prop: value, ...} }`.
 */
export function applyElementOverridesPerEid(instanceId, changesByEid, opts = {}) {
  const resolved = resolveSectionByInstance(instanceId);
  const entries = Object.entries(changesByEid || {}).filter(
    ([eid, changes]) => eid && changes && typeof changes === 'object'
  );
  if (!resolved || entries.length === 0) return false;

  const viewport = RESPONSIVE_VIEWPORTS.includes(opts.viewport) ? opts.viewport : null;
  let overrides = resolved.section.overrides || {};
  let changed = false;
  for (const [eid, changes] of entries) {
    const result = computeElementOverrides(overrides, eid, changes, viewport);
    overrides = result.overrides;
    if (result.changed) changed = true;
  }

  if (!changed && viewport) return false;
  const nextOverrides = Object.keys(overrides).length ? overrides : null;
  store.update(
    resolved.overridesPath || `${resolved.sectionPath}.overrides`,
    nextOverrides,
    opts.label || 'Change element styles'
  );
  for (const [eid, changes] of entries) {
    for (const field of Object.keys(changes)) {
      eventBus.emit('theme:override-changed', {
        scope: 'element',
        instanceId,
        eid,
        field,
        viewport,
        source: opts.source || 'element-controls'
      });
    }
  }
  requestElementEntranceRerender(entries.map(([, changes]) => changes));
  return true;
}

/**
 * Shared element-override mutation path for property and glass panels.
 * An all-size write also clears the same fields from named buckets so the new
 * value cannot be shadowed by an older viewport-specific edit.
 *
 * Since 16C2 this is a thin wrapper: when a sibling multi-selection is live and
 * the target has an equivalent inside the other units, the installed resolver
 * expands `eid` into every unit's counterpart and the write covers them all in
 * one entry. With no resolver installed — or no multi-selection — the expansion
 * is `[eid]` and behaviour is exactly what it was.
 */
export function applyElementOverrides(instanceId, eid, changes, opts = {}) {
  if (!eid) return false;
  const fanned = opts.noFanOut || !_restyleResolver ? null : _restyleResolver(instanceId, eid);
  const eids = Array.isArray(fanned) && fanned.length ? fanned : [eid];
  return applyElementOverridesAcross(instanceId, eids, changes, opts);
}

/**
 * Resolve an instanceId to its section data and state path.
 * Checks both shared sections (nav/footer) and page sections.
 * @param {string} instanceId
 * @returns {{ section: object, path: string, sectionPath: string, overridesPath: string } | null}
 */
export function resolveSectionByInstance(instanceId) {
  const state = store.getState();

  for (const type of ['navigation', 'footer']) {
    const shared = state.sharedSections[type];
    if (shared && shared.instanceId === instanceId) {
      const sectionPath = `sharedSections.${type}`;
      return {
        section: shared,
        path: `${sectionPath}.html`,
        sectionPath,
        overridesPath: `${sectionPath}.overrides`
      };
    }
  }

  const page = store.getActivePage();
  if (!page) return null;

  const sectionIndex = page.sections.findIndex((s) => s.instanceId === instanceId);
  if (sectionIndex === -1) return null;

  const pageIndex = state.pages.indexOf(page);
  const sectionPath = `pages.${pageIndex}.sections.${sectionIndex}`;
  return {
    section: page.sections[sectionIndex],
    path: `${sectionPath}.html`,
    sectionPath,
    overridesPath: `${sectionPath}.overrides`
  };
}
